import { useEffect, useRef } from 'preact/hooks';

/**
 * BottomFindBar — find-in-chat input that REUSES the bottom composer's pill slot.
 *
 * When chat-find is active, BottomComposer renders this in place of the normal
 * InputBar (#1433). It mirrors the composer pill's look (`.text-input`) and lives
 * in the same thumb-zone position, with ↑ / ↓ / ✕ affordances on the right.
 * Wired directly to the `useChatFind` hook — no new APIs invented:
 *   query → setQuery · goNext / goPrev · closeFind · matchIndex / matchCount.
 */
interface Props {
  query: string;
  matchIndex: number; // 0-based
  matchCount: number;
  onQueryChange: (q: string) => void;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
}

export function BottomFindBar({
  query,
  matchIndex,
  matchCount,
  onQueryChange,
  onNext,
  onPrev,
  onClose,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus when the find bar mounts (find just opened). rAF so the element
  // is laid out before focus — avoids iOS not surfacing the keyboard.
  useEffect(() => {
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, []);

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.shiftKey ? onPrev() : onNext();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }

  const countLabel =
    matchCount === 0
      ? query.trim() ? '0' : ''
      : `${matchIndex + 1}/${matchCount}`;

  return (
    <div class="bottom-find-bar" role="search" aria-label="Find in conversation">
      <span class="bottom-find-icon" aria-hidden="true">{'🔍'}</span>
      <input
        ref={inputRef}
        class="text-input bottom-find-input"
        type="text"
        value={query}
        placeholder="Find in chat…"
        onInput={(e) => onQueryChange((e.target as HTMLInputElement).value)}
        onKeyDown={handleKeyDown}
        spellcheck={false}
        autocomplete="off"
        enterkeyhint="search"
        aria-label="Search text"
      />
      {countLabel && (
        <span
          class={`bottom-find-count${matchCount === 0 ? ' bottom-find-count--none' : ''}`}
        >
          {countLabel}
        </span>
      )}
      <button
        type="button"
        class="bottom-find-btn"
        onClick={onPrev}
        disabled={matchCount === 0}
        title="Previous match (Shift+Enter)"
        aria-label="Previous match"
      >
        {'↑'}
      </button>
      <button
        type="button"
        class="bottom-find-btn"
        onClick={onNext}
        disabled={matchCount === 0}
        title="Next match (Enter)"
        aria-label="Next match"
      >
        {'↓'}
      </button>
      <button
        type="button"
        class="bottom-find-btn bottom-find-btn--close"
        onClick={onClose}
        title="Close (Esc)"
        aria-label="Close search"
      >
        {'✕'}
      </button>
    </div>
  );
}
